import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { usePageMeta } from "@/lib/seo";
import { useAuth } from "@/lib/auth";
import {
  getWatchlist, pauseWatch, resumeWatch, removeWatch, updateWatch,
  getAlerts, markAlertRead, markAllAlertsRead,
  startRescan, getAnalyzeStatus,
} from "@/lib/api";
import {
  Eye, RefreshCw, MoreVertical, Bell, TrendingUp, TrendingDown, Minus,
  ArrowRight, Loader2, PauseCircle, PlayCircle, Trash2, StickyNote, Tag, GitCompare,
} from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

function Movement({ value }) {
  if (value == null) return <span className="text-[#888]">—</span>;
  if (value > 0) return <span className="inline-flex items-center gap-1 text-[#1c7a1c] font-bold"><TrendingUp size={12} /> +{value}</span>;
  if (value < 0) return <span className="inline-flex items-center gap-1 text-[#FF331F] font-bold"><TrendingDown size={12} /> {value}</span>;
  return <span className="inline-flex items-center gap-1 text-[#888]"><Minus size={12} /> 0</span>;
}

function AlertCard({ alert, onRead, onViewDiff }) {
  return (
    <div
      className={`border-2 p-5 bg-white ${alert.read ? "border-[#ccc]" : "border-[#FF331F] shadow-brutal"}`}
      data-testid={`alert-card-${alert.alert_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#FF331F] font-bold">
          {alert.read ? "Change alert" : "● New change alert"} · {fmtDate(alert.created_at)}
        </div>
        {!alert.read && (
          <button
            onClick={() => onRead(alert.alert_id)}
            className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] hover:text-[#111] underline shrink-0"
            data-testid={`alert-mark-read-${alert.alert_id}`}
          >
            Mark read
          </button>
        )}
      </div>
      <h3 className="mt-2 font-serif-display text-xl font-black leading-tight">{alert.title}</h3>
      {alert.what_changed && (
        <p className="mt-2 text-sm text-[#333]"><strong>What changed:</strong> {alert.what_changed}</p>
      )}
      {alert.why_it_matters && (
        <p className="mt-1 text-sm text-[#333]"><strong>Why it matters:</strong> {alert.why_it_matters}</p>
      )}
      {alert.score_movement?.length > 0 && (
        <div className="mt-2 text-sm text-[#333]">
          <strong>Score movement:</strong>{" "}
          {alert.score_movement.map((m, i) => (
            <span key={i} className="inline-block mr-3 font-mono-plex text-xs">
              {m.monetization_type}: {m.new ? `new play at ${m.new_score}` : `${m.old_score} → ${m.new_score} (${m.delta > 0 ? "+" : ""}${m.delta})`}
            </span>
          ))}
        </div>
      )}
      {alert.evidence?.length > 0 && (
        <div className="mt-1 text-sm text-[#333]">
          <strong>Evidence found:</strong> {alert.evidence.join(" · ")}
        </div>
      )}
      {alert.next_action && (
        <p className="mt-1 text-sm text-[#333]"><strong>What to do next:</strong> {alert.next_action}</p>
      )}
      <button
        onClick={() => onViewDiff(alert)}
        className="mt-4 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-4 py-2 font-mono-plex text-[10px] uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
        data-testid={`alert-view-diff-${alert.alert_id}`}
      >
        <GitCompare size={12} /> View Full Diff
      </button>
    </div>
  );
}

function WatchRow({ w, onAction, busy }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editing, setEditing] = useState(null); // "note" | "tag" | null
  const [editVal, setEditVal] = useState("");

  const startEdit = (kind) => {
    setMenuOpen(false);
    setEditing(kind);
    setEditVal(kind === "note" ? (w.note || "") : "");
  };

  const saveEdit = () => {
    if (editing === "note") onAction("note", w, editVal);
    if (editing === "tag" && editVal.trim()) onAction("tag", w, editVal.trim());
    setEditing(null);
  };

  const paused = w.status === "paused";

  return (
    <div className={`border-2 border-[#111] bg-white p-5 ${paused ? "opacity-70" : ""}`} data-testid={`watch-row-${w.watch_id}`}>
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        {/* Domain + status */}
        <div className="lg:w-56 shrink-0">
          <div className="font-serif-display text-xl font-black leading-none break-all">{w.domain}</div>
          <div className="mt-1.5 flex items-center gap-2">
            <span className={`font-mono-plex text-[9px] uppercase tracking-widest px-1.5 py-0.5 ${paused ? "bg-[#888] text-white" : "bg-[#1c7a1c] text-white"}`}>
              {paused ? "Paused" : "Active"}
            </span>
            {w.tags?.map((t) => (
              <span key={t} className="font-mono-plex text-[9px] uppercase tracking-widest border border-[#111] px-1.5 py-0.5">{t}</span>
            ))}
          </div>
          {w.note && <div className="mt-1.5 text-xs text-[#555] italic" data-testid={`watch-note-${w.watch_id}`}>{w.note}</div>}
        </div>

        {/* Stats grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono-plex text-xs">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#888]">Top play</div>
            <div className="mt-0.5">
              {w.top_play ? `${w.top_play.monetization_type} · ${w.top_play.fit_score}` : "—"}{" "}
              <Movement value={w.score_movement} />
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#888]">Last change</div>
            <div className="mt-0.5 line-clamp-2">
              {w.last_change_headline ? `${w.last_change_headline} (${fmtDate(w.last_change_at)})` : "No meaningful changes yet"}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#888]">Last scan · Next auto</div>
            <div className="mt-0.5">{fmtDate(w.last_scan_at)} · {paused ? "paused" : fmtDate(w.next_auto_scan_at)}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[#888]">Manual rescan</div>
            <div className="mt-0.5">
              {paused ? "—" : w.manual_rescan_available ? "1 available" : `next ${fmtDate(w.manual_rescan_next_at)}`}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 shrink-0 relative">
          <Link
            to={`/report/${w.baseline_report_id}`}
            className="inline-flex items-center gap-1.5 border-2 border-[#111] px-3 py-2 font-mono-plex text-[10px] uppercase tracking-[0.2em] hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
            data-testid={`watch-view-diff-${w.watch_id}`}
          >
            <Eye size={11} /> View Diff
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="w-9 h-9 flex items-center justify-center border-2 border-[#111] bg-white hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
            data-testid={`watch-menu-${w.watch_id}`}
            aria-label="More actions"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <MoreVertical size={14} />}
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-11 z-20 w-48 bg-white border-2 border-[#111] shadow-brutal divide-y divide-[#eee]" data-testid={`watch-menu-panel-${w.watch_id}`}>
                {!paused && (
                  <button onClick={() => { setMenuOpen(false); onAction("rescan", w); }} className="w-full text-left px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0] flex items-center gap-2" data-testid={`watch-rescan-${w.watch_id}`}>
                    <RefreshCw size={11} /> Rescan Now
                  </button>
                )}
                <button onClick={() => { setMenuOpen(false); onAction(paused ? "resume" : "pause", w); }} className="w-full text-left px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0] flex items-center gap-2" data-testid={`watch-pause-${w.watch_id}`}>
                  {paused ? <><PlayCircle size={11} /> Resume monitoring</> : <><PauseCircle size={11} /> Pause monitoring</>}
                </button>
                <button onClick={() => startEdit("note")} className="w-full text-left px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0] flex items-center gap-2" data-testid={`watch-add-note-${w.watch_id}`}>
                  <StickyNote size={11} /> {w.note ? "Edit note" : "Add note"}
                </button>
                <button onClick={() => startEdit("tag")} className="w-full text-left px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0] flex items-center gap-2" data-testid={`watch-add-tag-${w.watch_id}`}>
                  <Tag size={11} /> Add tag
                </button>
                <button onClick={() => { setMenuOpen(false); onAction("remove", w); }} className="w-full text-left px-4 py-2.5 font-mono-plex text-[10px] uppercase tracking-widest text-[#FF331F] hover:bg-[#FF331F] hover:text-white flex items-center gap-2" data-testid={`watch-remove-${w.watch_id}`}>
                  <Trash2 size={11} /> Remove from Watchlist
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Inline note/tag editor */}
      {editing && (
        <div className="mt-4 flex items-center gap-2 border-t-2 border-dashed border-[#ccc] pt-4">
          <input
            autoFocus
            value={editVal}
            onChange={(e) => setEditVal(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && saveEdit()}
            placeholder={editing === "note" ? "Note for this URL..." : "Tag (e.g. client, competitor)"}
            className="flex-1 border-2 border-[#111] px-3 py-2 text-sm bg-white focus:outline-none"
            data-testid={`watch-edit-input-${w.watch_id}`}
          />
          <button onClick={saveEdit} className="bg-[#111] text-[#F4F4F0] px-4 py-2 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#FF331F]" data-testid={`watch-edit-save-${w.watch_id}`}>
            Save
          </button>
          <button onClick={() => setEditing(null)} className="border-2 border-[#111] px-4 py-2 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0]">
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

export default function Watchlist() {
  usePageMeta({
    title: "Watchlist — MonetizeGPS",
    description: "Your monitored websites: automatic monthly rescans, diffs and monetization change alerts.",
    path: "/watchlist",
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState({ items: [], unread_count: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [w, a] = await Promise.all([getWatchlist(), getAlerts()]);
      setData(w);
      setAlerts(a);
    } catch (_) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    refresh();
  }, [authLoading, user, refresh]);

  const handleAction = async (kind, w, value) => {
    try {
      if (kind === "pause") { await pauseWatch(w.watch_id); toast.success("Monitoring paused"); await refresh(); }
      if (kind === "resume") { await resumeWatch(w.watch_id); toast.success("Monitoring resumed"); await refresh(); }
      if (kind === "remove") { await removeWatch(w.watch_id); toast.success("Removed from Watchlist"); await refresh(); }
      if (kind === "note") { await updateWatch(w.watch_id, { note: value }); toast.success("Note saved"); await refresh(); }
      if (kind === "tag") { await updateWatch(w.watch_id, { tags: [...(w.tags || []), value].slice(0, 10) }); toast.success("Tag added"); await refresh(); }
      if (kind === "rescan") {
        setBusyId(w.watch_id);
        toast.info(`Rescanning ${w.domain}...`);
        const { job_id, free_rescan } = await startRescan(w.baseline_report_id);
        if (free_rescan) toast.success("Included monitoring rescan — no credit used");
        const startedAt = Date.now();
        while (Date.now() - startedAt < 5 * 60 * 1000) {
          await new Promise((r) => setTimeout(r, 2500));
          let s;
          try { s = await getAnalyzeStatus(job_id); } catch { continue; }
          if (s.status === "done" && s.report_id) {
            toast.success("Rescan complete");
            navigate(`/report/${s.report_id}`);
            return;
          }
          if (s.status === "error") throw new Error(s.error || "Rescan failed");
        }
        throw new Error("Rescan is taking longer than expected — check back in a minute.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const onRead = async (alertId) => {
    try {
      await markAlertRead(alertId);
      setAlerts((a) => ({
        unread_count: Math.max(0, a.unread_count - 1),
        items: a.items.map((x) => (x.alert_id === alertId ? { ...x, read: true } : x)),
      }));
      window.dispatchEvent(new Event("alerts-changed"));
    } catch (_) { /* silent */ }
  };

  const onViewDiff = (alert) => {
    if (!alert.read) onRead(alert.alert_id);
    navigate(`/report/${alert.report_id}`);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen grain">
        <Header />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm" data-testid="watchlist-loading">
          Loading Watchlist...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen grain">
        <Header />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 text-center" data-testid="watchlist-signed-out">
          <h1 className="font-serif-display text-3xl font-black">Sign in to see your Watchlist.</h1>
        </div>
      </div>
    );
  }

  const subActive = data?.subscription_active;
  const items = data?.items || [];
  const remaining = Math.max(0, (data?.limit || 0) - (data?.used || 0));

  return (
    <div className="min-h-screen grain" data-testid="watchlist-page">
      <Header />

      <section className="border-b-2 border-[#111]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-12 pb-8">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
            § Watchlist · Automatic monthly monitoring
          </div>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl font-black tracking-tighter leading-none">
            Watchlist
          </h1>
          {subActive ? (
            <p className="mt-3 font-mono-plex text-xs uppercase tracking-widest text-[#333]" data-testid="watchlist-usage">
              {data.used} of {data.limit} monitored websites used. · {remaining} Watchlist space{remaining === 1 ? "" : "s"} remaining.
            </p>
          ) : (
            <p className="mt-3 text-sm text-[#333]">
              Monitoring comes with monthly plans — every URL you scan is tracked automatically.
            </p>
          )}
        </div>
      </section>

      {/* Alerts */}
      {alerts.items.length > 0 && (
        <section className="border-b-2 border-[#111] bg-[#F4F4F0]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] inline-flex items-center gap-2">
                <Bell size={12} /> ▸ Change alerts {alerts.unread_count > 0 && <span className="bg-[#FF331F] text-white px-1.5 py-0.5">{alerts.unread_count} new</span>}
              </div>
              {alerts.unread_count > 0 && (
                <button
                  onClick={async () => { await markAllAlertsRead().catch(() => {}); window.dispatchEvent(new Event("alerts-changed")); refresh(); }}
                  className="font-mono-plex text-[10px] uppercase tracking-widest underline text-[#555] hover:text-[#111]"
                  data-testid="alerts-mark-all-read"
                >
                  Mark all read
                </button>
              )}
            </div>
            <div className="space-y-4" data-testid="alerts-list">
              {alerts.items.slice(0, 10).map((a) => (
                <AlertCard key={a.alert_id} alert={a} onRead={onRead} onViewDiff={onViewDiff} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Watch entries */}
      <section>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
          {!subActive ? (
            <div className="border-2 border-dashed border-[#888] p-10 text-center" data-testid="watchlist-upsell">
              <h2 className="font-serif-display text-2xl font-bold">Monitoring is a monthly-plan feature.</h2>
              <p className="mt-2 text-sm text-[#333] max-w-lg mx-auto">
                On any monthly plan, every website you scan is automatically tracked: a full rescan
                every month, a diff of what changed, and an alert when something meaningful moves.
              </p>
              <Link
                to="/pricing"
                className="mt-5 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
                data-testid="watchlist-upsell-cta"
              >
                See monthly plans <ArrowRight size={13} />
              </Link>
            </div>
          ) : items.length === 0 ? (
            <div className="border-2 border-dashed border-[#888] p-10 text-center" data-testid="watchlist-empty">
              <h2 className="font-serif-display text-2xl font-bold">No monitored websites yet.</h2>
              <p className="mt-2 text-sm text-[#333]">
                Scan any URL — it's automatically added to your Watchlist and tracked monthly.
              </p>
              <Link
                to="/"
                className="mt-5 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
                data-testid="watchlist-empty-cta"
              >
                Analyze a URL <ArrowRight size={13} />
              </Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="watchlist-items">
              {items.map((w) => (
                <WatchRow key={w.watch_id} w={w} onAction={handleAction} busy={busyId === w.watch_id} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
